import { Button, Chip, Switch, TextField, InputAdornment, Typography, Box, IconButton, Tooltip } from '@mui/material';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SftGlobalData from '../../library/SftGlobalData';
import axios from 'axios';
import crypto from 'crypto-js';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FloatingSaveButton from '../../components/FloatingSaveButton';
import Colors from '../../library/Colors';

const AiScreen = (props) => {
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [changedItems, setChangedItems] = useState(new Map());
    const [hasChanges, setHasChanges] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState('Detail');
    const [dryRunCount, setDryRunCount] = useState(null);
    const [dryRunLoading, setDryRunLoading] = useState(false);
    const params = useParams();
    let navigate = useNavigate();
    const apiRef = useGridApiRef();

    const handleSwitchChange = (id, field, value) => {
        // Update local state immediately for responsive UI
        setList(prevList => 
            prevList.map(item => 
                item.id === id ? { ...item, [field]: value ? 1 : 0 } : item
            )
        );

        // Track the change
        setChangedItems(prev => {
            const newMap = new Map(prev);
            const key = `${id}_${field}`;
            newMap.set(key, {
                id: id,
                type: 'switch',
                field: field,
                value: value ? 1 : 0
            });
            return newMap;
        });
        
        setHasChanges(true);
    };

    // Text change handler for free fields
    const handleTextChange = useCallback((id, value) => {
        // Update local state immediately for responsive UI
        setList(prevList => 
            prevList.map(item => 
                item.id === id ? { ...item, text_value: value } : item
            )
        );

        // Track the change
        setChangedItems(prev => {
            const newMap = new Map(prev);
            const key = `${id}_text_value`;
            newMap.set(key, {
                id: id,
                type: 'text',
                field: 'text_value',
                value: value
            });
            return newMap;
        });
        
        setHasChanges(true);
    }, []);

    // Save handler to process all changes
    const saveHandler = async () => {
        let successCount = 0;
        let errorCount = 0;

        // Process each change individually
        for (const [key, change] of changedItems) {
            try {
                let updateData;
                
                if (change.type === 'switch') {
                    updateData = {
                        key: props.api,
                        type: "ai_data_update",
                        action: "update",
                        id: change.id,
                        field: change.field,
                        value: change.value
                    };
                } else if (change.type === 'text') {
                    updateData = {
                        key: props.api,
                        type: "ai_data_update",
                        action: "update_text",
                        id: change.id,
                        text_value: change.value
                    };
                }

                const response = await SftGlobalData.post('sftData', updateData);

                if (response.data?.error) {
                    errorCount++;
                    if (SftGlobalData.debug) {
                        console.log('Save error for', key, response.data.error);
                    }
                } else {
                    successCount++;
                }
            } catch (error) {
                errorCount++;
                if (SftGlobalData.debug) {
                    console.log('Save exception for', key, error);
                }
            }
        }

        // Clear changes if all successful
        if (errorCount === 0) {
            setChangedItems(new Map());
            setHasChanges(false);
            props.notify(props.t('savedChanges') || 'Changes saved successfully', 2000, "bottom-center", props.theme, 2);
        } else if (successCount > 0) {
            props.notify(`${successCount} changes saved, ${errorCount} failed`, 5000, "bottom-center", props.theme, 1);
        } else {
            props.notify(props.t('errorOccured'), 5000, "bottom-center", props.theme, 1);
        }
    };

    // Separate free fields from regular fields
    const freeFields = useMemo(() => {
        return list
            .filter(item => item.data_field && item.data_field.match(/^free_field_[1-5]$/))
            .sort((a, b) => {
                const aNum = parseInt(a.data_field.match(/free_field_(\d+)/)[1]);
                const bNum = parseInt(b.data_field.match(/free_field_(\d+)/)[1]);
                return aNum - bNum;
            });
    }, [list]);

    // Filter the list based on search term (excluding free fields)
    const filteredList = useMemo(() => {
        let resultList = list.filter(item => 
            !item.data_field || !item.data_field.match(/^free_field_[1-5]$/)
        );
        
        // Apply search filter if there's a search term
        if (searchTerm) {
            resultList = resultList.filter(item => {
                const searchLower = searchTerm.toLowerCase();
                
                // Search in the data_field value
                const fieldMatch = item.data_field?.toLowerCase().includes(searchLower);
                
                // Special handling for category_X_web fields
                let categoryWebMatch = false;
                if (item.data_field && item.data_field.match(/^category_(\d+)_web$/)) {
                    const categoryNumber = item.data_field.match(/^category_(\d+)_web$/)[1];
                    const categoryDescField = `category_${categoryNumber}_descr`;
                    const categoryDesc = item[categoryDescField];
                    
                    if (categoryDesc && categoryDesc.trim() !== '') {
                        const categoryWebLabel = `${categoryDesc} (web)`;
                        categoryWebMatch = categoryWebLabel.toLowerCase().includes(searchLower);
                    }
                }
                
                // Special handling for category_X_lang_Y fields
                let categoryLangMatch = false;
                if (item.data_field && item.data_field.match(/^category_(\d+)_lang_(\d+)$/)) {
                    const matches = item.data_field.match(/^category_(\d+)_lang_(\d+)$/);
                    const categoryNumber = matches[1];
                    const langNumber = matches[2];
                    const categoryDescField = `category_${categoryNumber}_descr`;
                    const categoryDesc = item[categoryDescField];
                    
                    if (categoryDesc && categoryDesc.trim() !== '') {
                        const categoryLangLabel = `${categoryDesc} (taal ${langNumber})`;
                        categoryLangMatch = categoryLangLabel.toLowerCase().includes(searchLower);
                    }
                }
                
                // Search in the description field if it exists
                const descriptionField = `${item.data_field}_descr`;
                const description = item[descriptionField];
                const descriptionMatch = description && description.toLowerCase().includes(searchLower);
                
                // Also search in the translated field name
                const translationKey = `ai_field_${item.data_field}`;
                const translatedName = props.t(translationKey).toLowerCase();
                const translationMatch = translatedName.includes(searchLower);
                
                return fieldMatch || categoryWebMatch || categoryLangMatch || descriptionMatch || translationMatch;
            });
        }
        
        return resultList;
    }, [list, searchTerm, props.t]);

    // Group items by category and determine which categories need grouping
    const { groupedData, categoryGroups } = useMemo(() => {
        // Count items per category
        const categoryCounts = {};
        filteredList.forEach(item => {
            const category = item.category || 'Uncategorized';
            categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        });

        // Create group data structure
        const groups = {};
        const flatData = [];
        let currentId = 1;

        // Sort categories to ensure consistent ordering
        const sortedCategories = Object.keys(categoryCounts).sort((a, b) => {
            // Get the first item of each category to compare sort values
            const itemA = filteredList.find(item => (item.category || 'Uncategorized') === a);
            const itemB = filteredList.find(item => (item.category || 'Uncategorized') === b);
            const sortA = itemA ? itemA.sort : 999;
            const sortB = itemB ? itemB.sort : 999;
            return sortA - sortB;
        });

        sortedCategories.forEach(category => {
            const categoryItems = filteredList.filter(item => 
                (item.category || 'Uncategorized') === category
            );

            if (categoryCounts[category] > 1) {
                // Create group header
                const groupId = `group-${category}`;
                const groupHeader = {
                    id: groupId,
                    isGroup: true,
                    category: category,
                    itemCount: categoryItems.length,
                    _gridId: currentId++
                };
                flatData.push(groupHeader);
                groups[category] = true;

                // Add items if category is expanded
                if (expandedCategory === category) {
                    categoryItems.forEach(item => {
                        flatData.push({
                            ...item,
                            _gridId: currentId++,
                            _groupCategory: category
                        });
                    });
                }
            } else {
                // Single item - no grouping needed
                categoryItems.forEach(item => {
                    flatData.push({
                        ...item,
                        _gridId: currentId++
                    });
                });
            }
        });

        return { groupedData: flatData, categoryGroups: groups };
    }, [filteredList, expandedCategory]);

    const handleCategoryToggle = (category) => {
        setExpandedCategory(prevCategory => {
            if (prevCategory === category) {
                // Clicking on already expanded category - collapse it
                return null;
            } else {
                // Clicking on different category - expand it
                return category;
            }
        });
    };

    // Generate prompt preview
    const promptPreview = useMemo(() => {
        // Get free fields with values
        const fieldsWithValues = freeFields.filter(item => 
            item.text_value && 
            item.text_value.trim() !== ''
        );
        
        // Get non-translatable fields that are used
        const nonTranslatableFields = list.filter(item => 
            item.is_used === 1 || item.is_used === '1'
        ).filter(item =>
            (item.is_translatable === 0 || item.is_translatable === '0') &&
            (!item.data_field || !item.data_field.match(/^free_field_[1-5]$/))
        );
        
        let prompt = "Geef een SEO tekst voor een webshop op basis van de onderstaande data. Focus op search-engine optimaliteit en zoekwoorden die conversie verhogen. Beschrijf de specifieke print, kleuren en unieke details (zoals contrastboorden of knopen) die zichtbaar zijn op de afbeelding of in de omschrijving. Gebruik geen HTML-tags, maar streepjes (-) en enters. ";
        
        if (fieldsWithValues.length === 0) {
            prompt += "(geen waarden ingesteld)";
        } else {
            const values = fieldsWithValues.map(field => field.text_value.trim()).join(', ');
            prompt += values;
        }
        
        // Add non-translatable fields instruction
        if (nonTranslatableFields.length > 0) {
            prompt += ". Vertaal de volgende velden NIET: ";
            
            const fieldNames = nonTranslatableFields.map(field => {
                // Use description if available (for detail_1, category_1, etc.)
                const descriptionField = `${field.data_field}_descr`;
                const description = field[descriptionField];
                
                if (description && description.trim() !== '') {
                    return description;
                }
                
                // Special handling for category_X_web fields
                if (field.data_field && field.data_field.match(/^category_(\d+)_web$/)) {
                    const categoryNumber = field.data_field.match(/^category_(\d+)_web$/)[1];
                    const categoryDescField = `category_${categoryNumber}_descr`;
                    const categoryDesc = field[categoryDescField];
                    
                    if (categoryDesc && categoryDesc.trim() !== '') {
                        return `${categoryDesc} (web)`;
                    }
                }
                
                // Special handling for category_X_lang_Y fields
                if (field.data_field && field.data_field.match(/^category_(\d+)_lang_(\d+)$/)) {
                    const matches = field.data_field.match(/^category_(\d+)_lang_(\d+)$/);
                    const categoryNumber = matches[1];
                    const langNumber = matches[2];
                    const categoryDescField = `category_${categoryNumber}_descr`;
                    const categoryDesc = field[categoryDescField];
                    
                    if (categoryDesc && categoryDesc.trim() !== '') {
                        return `${categoryDesc} (taal ${langNumber})`;
                    }
                }
                
                // Otherwise use the field name itself
                return field.data_field;
            });
            
            prompt += fieldNames.join(', ');
        }
        
        return prompt;
    }, [freeFields, list]);

    const gridColumns = [
        {
            field: 'id',
            headerName: "ID",
            width: 75,
            align: "left",
            renderCell: (params) => {
                if (params.row.isGroup) {
                    return null;
                }
                return params.value;
            }
        },
        {
            field: 'data_field',
            headerName: props.t('ai_dataField'),
            headerClassName: 'bold',
            minWidth: 200,
            width: 400,
            align: "left",
            renderCell: (params) => {
                // Handle group headers
                if (params.row.isGroup) {
                    const isExpanded = expandedCategory === params.row.category;
                    const translationKey = `ai_category_${params.row.category}`;
                    const categoryName = props.t(translationKey);
                    
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', fontWeight: 'bold' }}>
                            <IconButton 
                                size="small" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCategoryToggle(params.row.category);
                                }}
                                sx={{ mr: 1 }}
                            >
                                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                            </IconButton>
                            {categoryName}
                        </Box>
                    );
                }

                // Handle indentation for grouped items
                const isGroupedItem = params.row._groupCategory;
                const cellStyle = isGroupedItem ? { paddingLeft: '40px' } : {};

                // Special handling for category_X_web fields
                if (params.value && params.value.match(/^category_(\d+)_web$/)) {
                    const categoryNumber = params.value.match(/^category_(\d+)_web$/)[1];
                    const categoryDescField = `category_${categoryNumber}_descr`;
                    const categoryDesc = params.row[categoryDescField];
                    
                    if (categoryDesc && categoryDesc.trim() !== '') {
                        return <div style={cellStyle}>{categoryDesc} (web)</div>;
                    }
                }
                
                // Special handling for category_X_lang_Y fields
                if (params.value && params.value.match(/^category_(\d+)_lang_(\d+)$/)) {
                    const matches = params.value.match(/^category_(\d+)_lang_(\d+)$/);
                    const categoryNumber = matches[1];
                    const langNumber = matches[2];
                    const categoryDescField = `category_${categoryNumber}_descr`;
                    const categoryDesc = params.row[categoryDescField];
                    
                    if (categoryDesc && categoryDesc.trim() !== '') {
                        return <div style={cellStyle}>{categoryDesc} (taal {langNumber})</div>;
                    }
                }
                
                // Check if there's a description field for this data_field
                const descriptionField = `${params.value}_descr`;
                const description = params.row[descriptionField];
                
                // Use description if it exists and is not null/empty, otherwise use translation
                if (description && description.trim() !== '') {
                    return <div style={cellStyle}>{description}</div>;
                }
                
                const translationKey = `ai_field_${params.value}`;
                return <div style={cellStyle}>{props.t(translationKey)}</div>;
            }
        },
        {
            field: 'is_used',
            headerName: props.t('ai_isUsed'),
            headerAlign: 'center',
            width: 120,
            align: "center",
            renderCell: (params) => {
                if (params.row.isGroup) {
                    return null;
                }
                return (
                    <Switch
                        checked={params.row.is_used === 1 || params.row.is_used === '1'}
                        onChange={(e) => handleSwitchChange(params.row.id, 'is_used', e.target.checked)}
                        inputProps={{ 'aria-label': 'is used' }}
                    />
                );
            }
        },
        {
            field: 'is_translatable',
            headerName: props.t('ai_isTranslateable'),
            headerAlign: 'center',
            width: 150,
            align: "center",
            renderCell: (params) => {
                if (params.row.isGroup) {
                    return null;
                }
                return (
                    <Switch
                        checked={params.row.is_translatable === 1 || params.row.is_translatable === '1'}
                        onChange={(e) => handleSwitchChange(params.row.id, 'is_translatable', e.target.checked)}
                        inputProps={{ 'aria-label': 'is translatable' }}
                    />
                );
            }
        },
        {
            field: 'is_required',
            headerName: props.t('ai_isRequired'),
            headerAlign: 'center',
            width: 120,
            align: "center",
            renderCell: (params) => {
                if (params.row.isGroup) {
                    return null;
                }
                return (
                    <Switch
                        checked={params.row.is_required === 1 || params.row.is_required === '1'}
                        onChange={(e) => handleSwitchChange(params.row.id, 'is_required', e.target.checked)}
                        inputProps={{ 'aria-label': 'is required' }}
                    />
                );
            }
        }
    ];

    useEffect(() => {
        if (props.api !== '')
            loadData();
    }, [props.api]);

    const loadData = () => {
        setLoading(true);
        SftGlobalData.post('sftData', {key: props.api, type: "ai_data"}).then(response => {
            if (typeof response.error === 'undefined') {
                if(typeof response.data?.error === 'undefined')
                    setList(response.data);
                else
                    props.notify(props.t('errorOccured') + " " + response.data.error, 7500, "top-center", props.theme, 1);
            } else {
                props.notify(props.t('errorOccured'), 7500, "top-center", props.theme, 1);
            }
            setLoading(false);
        });
        props.changeScreen('Licence');
    }

    const fetchDryRun = () => {
        if (!props.db?.client_id || !props.db?.db_id) return;
        setDryRunLoading(true);
        const currentYear = new Date().getFullYear();
        const salt = `ai-${currentYear}`;
        const hash = crypto.SHA256(`${SftGlobalData.ai_password}${salt}`).toString(crypto.enc.Hex);

        axios.post('https://ai.softtouch.eu/api/aitext/dry-run', {
            password: hash,
            dbId: props.db.db_id,
            clientId: props.db.client_id,
            includeInactive: true
        }, {
            headers: { 'Content-Type': 'application/json' }
        }).then(response => {
            setDryRunCount(response.data);
        }).catch(error => {
            console.log('Dry run error:', error);
            props.notify(props.t('errorOccured'), 5000, "top-center", props.theme, 1);
        }).finally(() => {
            setDryRunLoading(false);
        });
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }} className={(props.screenWidth) < 600 ? 'not_expanded container_content _80perc ' + props.theme + ' container_content_' + props.theme : 'expanded container_content _80perc ' + props.theme + ' container_content_' + props.theme} >
            <div className={"container_list__title " + props.theme} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <h2>A.I.</h2>
                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    {dryRunCount !== null && (
                        <Chip label={`${dryRunCount?.totalProducts ?? 0} products`} color="primary" variant="outlined" />
                    )}
                    <Tooltip title={props.t('ai_dry_run_tooltip')} arrow>
                        <IconButton onClick={fetchDryRun} disabled={dryRunLoading} size="small">
                            <VisibilityIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
            <div style={{ padding: '0 20px 20px 20px', color: props.theme === 'dark' ? '#ccc' : '#666' }}>
                <p>{props.t('ai_description')}</p>
            </div>
            {/* Free Fields Section */}
            <div style={{ padding: '20px' }}>
                <Typography variant="h6" gutterBottom style={{ color: props.theme === 'dark' ? '#fff' : '#000' }}>
                    {props.t('ai_freeFieldsTitle') || 'Free Fields'}
                </Typography>
                <hr/>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {freeFields.map((field) => {
                        const currentValue = field.text_value || '';
                        const translationKey = `ai_field_${field.data_field}`;
                        const label = props.t(translationKey);
                        
                        return (
                            <Box key={field.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: props.theme === 'dark' ? '#ccc' : '#666' }}>
                                    {label}
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={currentValue}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        if (newValue.length <= 250) {
                                            handleTextChange(field.id, newValue);
                                        }
                                    }}
                                    placeholder={props.t('ai_freeFieldPlaceholder') || 'Enter text for AI generation...'}
                                    inputProps={{ maxLength: 250 }}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        backgroundColor: props.theme === 'dark' ? '#2d2d2d' : '#fff',
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: props.theme === 'dark' ? '#555' : '#ddd',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: props.theme === 'dark' ? '#777' : '#bbb',
                                            },
                                        },
                                    }}
                                />
                                <Typography variant="caption" sx={{ textAlign: 'right', color: 'text.secondary' }}>
                                    {currentValue.length}/250
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </div>
            
            {/* Prompt Preview */}
            <div style={{ padding: '20px' }}>
                <Typography variant="h6" gutterBottom style={{ color: props.theme === 'dark' ? '#fff' : '#000' }}>
                    {props.t('ai_promptPreviewTitle')}
                </Typography>
                <hr/>
                <Box 
                    sx={{ 
                        padding: '16px', 
                        border: '1px solid', 
                        borderColor: props.theme === 'dark' ? '#555' : '#ddd',
                        borderRadius: '4px',
                        backgroundColor: props.theme === 'dark' ? '#2d2d2d' : '#f9f9f9',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        color: props.theme === 'dark' ? '#ccc' : '#333',
                        minHeight: '60px'
                    }}
                >
                    {promptPreview}
                </Box>
            </div>
            
            {/* Search and DataGrid Section */}
            <div style={{ padding: '20px' }}>
                <Typography variant="h6" gutterBottom style={{ color: props.theme === 'dark' ? '#fff' : '#000' }}>
                    {props.t('ai_data_fields')}
                </Typography>
                <hr/>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={props.t('ai_searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    size="small"
                />
            </div>
            <div style={{ padding: '0 20px 20px 20px' }}>
                <DataGridPro
                    apiRef={apiRef}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                id: false
                            }
                        }
                    }}
                    className={'excel_grid ' + props.theme}
                    columns={gridColumns}
                    rows={groupedData}
                    getRowId={(row) => row._gridId || row.id}
                    rowThreshold={0}
                    rowHeight={38}
                    autoHeight={true}
                    loading={loading}
                    pagination={false}
                    hideFooter={true}
                    onRowClick={(params) => {
                        if (params.row.isGroup) {
                            handleCategoryToggle(params.row.category);
                        }
                    }}
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'default',
                        },
                        '& .MuiDataGrid-row.group': {
                            backgroundColor: props.theme === 'dark' ? '#3a3a3a' : '#f5f5f5',
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: props.theme === 'dark' ? '#444' : '#e8e8e8',
                            }
                        }
                    }}
                    getRowClassName={(params) => {
                        return params.row.isGroup ? 'group' : '';
                    }}
                />
            </div>
            
            {hasChanges && (
                <FloatingSaveButton onClick={saveHandler} />
            )}
        </div>
    );
}

export default AiScreen;
